import { Backdrop, CircularProgress } from "@mui/material";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { theme } from "../../Customization/Theme";
import { BulkUpdateNetworkService } from "../../Store/Slices/BulkUpdate/bulkUpdateNetworkService";
import { useNotification } from "../useNotification/useNotification";
import { registerBulkUpdateTracker } from "./bulkUpdateTracker";

const STORAGE_KEY = "geta_bulk_update_job";
// A job still running an hour after the tab was reloaded is almost certainly stuck.
// Resuming it would put the blocking backdrop back up for a job nobody remembers
// starting; leaving the grid stale until a manual refresh is the safer failure.
const MAX_RESUME_AGE_MS = 60 * 60 * 1000;
const MAX_POLL_MS = 10 * 60 * 1000;
// Small jobs finish on the first poll; the backoff keeps them feeling instant
// without hammering the API for the long ones.
const POLL_BACKOFF_MS = [1000, 2000, 3000, 5000, 8000];

export const BULK_UPDATE_COMPLETE_EVENT = "geta:bulk-update-complete";

type BulkUpdateStatus = "success" | "failed" | "timeout";

interface TrackOptions {
  message?: string;
  module?: string;
  onComplete?: (status: BulkUpdateStatus) => void;
}

interface BulkUpdateJob {
  taskId: string;
  startedAt: number;
  message: string;
  module?: string;
}

// The job is tracked app-wide, but only the grid it writes to is worth blocking —
// freezing Inbox or Broadcast because contacts are being reassigned helps nobody.
const MODULE_ROUTES: Record<string, string> = {
  contact: "/contacts",
};

interface BulkUpdateContextValue {
  trackBulkUpdate: (taskId: string, options?: TrackOptions) => void;
  isBulkUpdateRunning: boolean;
}

const BulkUpdateContext = createContext<BulkUpdateContextValue>({
  trackBulkUpdate: () => {},
  isBulkUpdateRunning: false,
});

export const useBulkUpdate = () => useContext(BulkUpdateContext);

export const BulkUpdateProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { showAlert } = useNotification();
  const location = useLocation();
  const [activeJob, setActiveJob] = useState<BulkUpdateJob | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const timerRef = useRef<any>(null);
  const onCompleteRef = useRef<((status: BulkUpdateStatus) => void) | undefined>(
    undefined,
  );

  const finish = useCallback(
    (status: BulkUpdateStatus, data: any) => {
      window.clearTimeout(timerRef.current);
      localStorage.removeItem(STORAGE_KEY);
      setActiveJob(null);
      setProgress(0);

      const onComplete = onCompleteRef.current;
      onCompleteRef.current = undefined;

      if (status === "success") {
        showAlert(data?.message || "Bulk update completed", "success");
      } else if (status === "failed") {
        showAlert(
          data?.message || "Bulk update failed and may be partially applied",
          "error",
        );
      } else {
        showAlert(
          "Bulk update is still running, the list will show the changes once it finishes",
          "warning",
        );
      }

      onComplete?.(status);
      window.dispatchEvent(
        new CustomEvent(BULK_UPDATE_COMPLETE_EVENT, { detail: { status, data } }),
      );
    },
    [showAlert],
  );

  useEffect(() => {
    if (!activeJob) return;

    let cancelled = false;
    let attempt = 0;

    const schedule = () => {
      if (cancelled) return;
      if (Date.now() - activeJob.startedAt > MAX_POLL_MS) {
        finish("timeout", null);
        return;
      }
      const delay =
        POLL_BACKOFF_MS[Math.min(attempt, POLL_BACKOFF_MS.length - 1)];
      attempt += 1;
      timerRef.current = window.setTimeout(poll, delay);
    };

    const poll = () => {
      BulkUpdateNetworkService.getBulkUpdateStatus(activeJob.taskId)
        .then((response: any) => {
          if (cancelled) return;
          const data = response?.data;
          if (data?.bulk_status === "success") {
            finish("success", data);
          } else if (data?.bulk_status === "failed") {
            finish("failed", data);
          } else {
            if (data?.bulk_status === "processing") {
              // matched counts records processed, so it advances even when a batch
              // writes values a record already had; modified would sit at 0
              setProgress(data?.matched ?? 0);
            }
            schedule();
          }
        })
        .catch((error: any) => {
          if (cancelled) return;
          // A dropped poll must not abandon a job that is still running, but a
          // rejected or unknown task id answers the same way every time — retrying
          // it holds the backdrop up for the full MAX_POLL_MS over nothing.
          const status = error?.response?.status;
          if (status === 404 || status === 400) {
            finish("failed", { message: "Bulk update could not be tracked" });
            return;
          }
          schedule();
        });
    };

    schedule();
    return () => {
      cancelled = true;
      window.clearTimeout(timerRef.current);
    };
  }, [activeJob, finish]);

  // A reload or a new tab loses the in-memory job but not the running task.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;
      const job: BulkUpdateJob = JSON.parse(stored);
      if (!job?.taskId || Date.now() - job.startedAt > MAX_RESUME_AGE_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      setActiveJob(job);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // An already-open tab never re-reads storage on its own, so without this it stays
  // interactive while another tab's bulk update is mid-flight — and starting a second
  // one there would overwrite the key and orphan the first job.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      setProgress(0);
      if (!event.newValue) {
        // the other tab reached the terminal state first and cleared the key, so this
        // one never polls it through finish() — refresh the grid without a second toast
        setActiveJob(null);
        window.dispatchEvent(
          new CustomEvent(BULK_UPDATE_COMPLETE_EVENT, { detail: { status: "observed" } }),
        );
        return;
      }
      try {
        const job: BulkUpdateJob = JSON.parse(event.newValue);
        if (job?.taskId) {
          setActiveJob(job);
        }
      } catch {
        setActiveJob(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const trackBulkUpdate = useCallback(
    (taskId: string, options: TrackOptions = {}) => {
      if (!taskId) return;
      // The backdrop only covers the grid the running job writes to, so a second tab
      // on another module's grid is free to start one over the top. Whether a job is
      // running is a fact about the stored job, not about the route being viewed.
      // This fires on the response, so the second job is already queued server-side —
      // keeping the first tracked is what stops it being orphaned mid-flight.
      if (localStorage.getItem(STORAGE_KEY)) {
        showAlert(
          "Another bulk update is still running. This one is queued and will apply in the background",
          "warning",
        );
        return;
      }
      const job: BulkUpdateJob = {
        taskId,
        startedAt: Date.now(),
        message: options.message || "Bulk update in progress",
        module: options.module,
      };
      onCompleteRef.current = options.onComplete;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(job));
      setProgress(0);
      setActiveJob(job);
    },
    [showAlert],
  );

  useEffect(() => {
    registerBulkUpdateTracker(trackBulkUpdate);
    return () => registerBulkUpdateTracker(null);
  }, [trackBulkUpdate]);

  // An unknown module blocks nothing rather than everything: a new bulk endpoint that
  // this map does not know about must not freeze the whole app by accident.
  const blockedRoute = activeJob?.module
    ? MODULE_ROUTES[activeJob.module]
    : undefined;
  const showBackdrop = Boolean(
    activeJob && blockedRoute && location.pathname.startsWith(blockedRoute),
  );

  // useLocation re-renders this provider on every navigation, so an inline object
  // here would hand every consumer a new value on each route change
  const contextValue = useMemo(
    () => ({ trackBulkUpdate, isBulkUpdateRunning: Boolean(activeJob) }),
    [trackBulkUpdate, activeJob],
  );

  return (
    <BulkUpdateContext.Provider value={contextValue}>
      {activeJob && showBackdrop && (
        <Backdrop
          sx={{
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: (muiTheme) => muiTheme.zIndex.drawer + 2,
          }}
          open={true}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "1.5rem",
            }}
          >
            <CircularProgress
              color="primary"
              style={{ color: `${theme.palette.primary.main} !important` }}
            />
            <p style={{ color: "white", fontSize: "1rem" }}>
              {progress > 0
                ? `${activeJob.message} — ${progress} records updated`
                : activeJob.message}
            </p>
            <p style={{ color: "white", fontSize: "0.75rem" }}>
              Please do not close this window
            </p>
          </div>
        </Backdrop>
      )}
      {children}
    </BulkUpdateContext.Provider>
  );
};
